import React, { useState, useEffect } from 'react';
import ProductDialogComponent from './ProductDialog.component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useDateFns } from '../../../../Hooks';
import { bindActionCreators } from 'redux';
import {
  requestLoadProductsForEvent,
  requestPaymentMethods,
  requestCustomerById,
  requestCreatePaymentAction,
  requestCreateOrderlineAction,
} from '../../../../store';
import { useTranslation } from 'react-i18next';
import { IOrderline } from '../../../../Models/DTO/Orderline.models';
import { REQUEST_ACTIONS } from '../../../../Constants/Actions.constants';
import { selectEventProducts } from '../../../../store/products';
import { selectAvailablePaymentMethod } from '../../../../store/payments';
import { selectCustomer } from '../../../../store/customers';
import { selectLoading } from '../../../../store/app';
import { PaymentMethodEnumType } from '../../../../Models/DTO/PaymentMethod.model';
import { SelectChangeEvent } from '@mui/material';

export const useProductDialogContainer = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodEnumType>('');
  const [product, setProduct] = useState(-1);

  const { getParams, goBack } = useNavigation();
  const { nowAsUTC } = useDateFns();
  const { domain, customerId, id } = getParams();

  const loadEventProducts = bindActionCreators(requestLoadProductsForEvent, dispatch);
  const loadAvailablePaymentMethods = bindActionCreators(requestPaymentMethods, dispatch);
  const loadCustomer = bindActionCreators(requestCustomerById, dispatch);
  const createPayment = bindActionCreators(requestCreatePaymentAction, dispatch);
  const createOrderline = bindActionCreators(requestCreateOrderlineAction, dispatch);

  const availableProducts = useSelector(selectEventProducts);
  const availablePaymentMethods = useSelector(selectAvailablePaymentMethod);
  const customer = useSelector(selectCustomer(customerId || ''));
  const isOrderProcessing = useSelector(selectLoading(REQUEST_ACTIONS.CREATE_ORDERLINE));
  const isPaymentProcessing = useSelector(
    selectLoading(REQUEST_ACTIONS.CREATE_PAYMENT_FOR_ORDERLINE),
  );

  useEffect(() => {
    if (availableProducts.length === 0) {
      // TODO: Prepare this to also work from the customer page without an eventId
      loadEventProducts(id || '');
    }

    if (availablePaymentMethods.length === 0) {
      loadAvailablePaymentMethods(domain || '');
    }

    if (!customer) {
      loadCustomer(customerId || '');
    }
  }, []);

  const handleProductSelection = (ev: SelectChangeEvent<number>) => {
    setProduct(ev.target.value as number);
  };

  const handlePaymentMethodSelection = (ev: SelectChangeEvent<PaymentMethodEnumType>) => {
    setPaymentMethod(ev.target.value as PaymentMethodEnumType);
  };

  const handleAddProduct = async () => {
    const customerId = customer?.id ?? 0;

    const orderline = await createOrderline(
      {
        productId: product,
        paymentType: paymentMethod,
        eventId: id,
        domain,
        tempDisablePayment: ['Cash', 'EC'].includes(paymentMethod),
      },
      customerId,
    );

    if (!!orderline) {
      if (['PayLater', 'CashTransfer'].includes(paymentMethod)) {
        goBack();
      } else {
        const payment = await createPayment(
          {
            paymentDateUTC: nowAsUTC(),
            paymentType: paymentMethod,
            type: paymentMethod,
            amount: (orderline as Partial<IOrderline>)?.lineAmount ?? 0,
          },
          (orderline as Partial<IOrderline>)?.id ?? 0,
        );

        if (!!payment) {
          goBack();
        }
      }
    }
  };

  return {
    availableProducts,
    availablePaymentMethods,
    customer,
    paymentMethod,
    product,
    handleProductSelection,
    handlePaymentMethodSelection,
    handleAddProduct,
    processing: (isOrderProcessing || isPaymentProcessing) && paymentMethod === '',
    addProductButtonDisabled: (product ?? -1) < 0 || paymentMethod === '',
    t,
  };
};

const ProductDialogContainer = () => {
  const {
    availableProducts,
    availablePaymentMethods,
    paymentMethod,
    handleAddProduct,
    handleProductSelection,
    handlePaymentMethodSelection,
    processing,
    addProductButtonDisabled,
    product,
    customer,
    t,
  } = useProductDialogContainer();

  return (
    <ProductDialogComponent
      addProductButtonDisabled={addProductButtonDisabled}
      availablePaymentMethods={availablePaymentMethods}
      availableProducts={availableProducts}
      customer={customer}
      onAddProduct={handleAddProduct}
      onSelectPaymentMethod={handlePaymentMethodSelection}
      onSelectProduct={handleProductSelection}
      processing={processing}
      selectedPaymentMethod={paymentMethod}
      selectedProduct={product}
      t={t}
    />
  );
};

export default ProductDialogContainer;
